import React, { useState, useEffect } from 'react';
import { Users, Shield, ShieldAlert, UserCog, UserCheck, Trash2, Search, X, Check, Mail } from 'lucide-react';
import { collection as firestoreCollection, onSnapshot as firestoreOnSnapshot, doc as firestoreDoc, setDoc as firestoreSetDoc, deleteDoc as firestoreDeleteDoc, updateDoc as firestoreUpdateDoc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { Player } from '../../types';

interface StaffMember {
  email: string;
  role: 'co_admin' | 'empleado' | 'gestor_turnos' | 'gestor_torneos';
  pin?: string;
  addedAt: number;
}

interface AdminStaffProps {
  db: any;
  getCollectionPath: (col: string) => string;
  players: Player[];
  userId?: string;
}

export function AdminStaff({ db, getCollectionPath, players, userId }: AdminStaffProps) {
  const [staffList, setStaffList] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Form State
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [manualEmail, setManualEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState<StaffMember['role'] | ''>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // AutoComplete logic
  const [showDropdown, setShowDropdown] = useState(false);
  const [adminPin, setAdminPin] = useState('');
  const [editingPinId, setEditingPinId] = useState<string | null>(null);
  
  const filteredPlayers = players.filter(p => 
    !p.isGuest && 
    (p.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
     (p as any).email?.toLowerCase().includes(searchQuery.toLowerCase()))
  ).slice(0, 5);

  useEffect(() => {
    if (!userId) return;
    
    // Fetch Admin Pin
    const fetchAdminPin = async () => {
      try {
        const uDoc = await getDoc(firestoreDoc(db, 'users', userId));
        if (uDoc.exists() && uDoc.data().posPin) {
          setAdminPin(uDoc.data().posPin);
        }
      } catch (e) { console.error(e); }
    };
    fetchAdminPin();

    const collRef = firestoreCollection(db, 'staff_roles');
    const q = query(collRef, where('clubId', '==', userId));
    const unsub = firestoreOnSnapshot(q, (snap) => {
      const data = snap.docs.map(d => ({
        email: d.id,
        ...d.data()
      })) as StaffMember[];
      setStaffList(data);
      setLoading(false);
    });
    return () => unsub();
  }, [db, userId]);

  const handleAddStaff = async () => {
    let targetEmail = '';
    
    if (selectedPlayer) {
      // We need to get the player's email. If it's not in the Player object, 
      // we might need to fetch it from 'users' collection using player.id
      try {
         // Wait, Player might not have email. Let's try to get it from users.
         // Wait! We can just fetch the user doc directly if we have the player ID
         const userSnap = await getDoc(firestoreDoc(db, 'users', selectedPlayer.id));
         if (userSnap.exists() && userSnap.data().email) {
           targetEmail = userSnap.data().email.toLowerCase().trim();
         } else {
           alert("Este jugador no tiene un correo electrónico válido registrado.");
           return;
         }
      } catch(e) {
         console.error(e);
         alert("Error al buscar el correo del jugador.");
         return;
      }
    } else if (manualEmail) {
      targetEmail = manualEmail.toLowerCase().trim();
    } else {
      alert("Debes seleccionar un jugador o ingresar un correo electrónico.");
      return;
    }

    if (!targetEmail || !targetEmail.includes('@')) {
      alert("Por favor ingresa un correo electrónico válido.");
      return;
    }

    if (!selectedRole) {
      alert("Por favor selecciona un rol para este miembro.");
      return;
    }

    setIsSubmitting(true);
    try {
      await firestoreSetDoc(firestoreDoc(db, 'staff_roles', targetEmail), {
        role: selectedRole,
        clubId: userId || '',
        pin: null,
        addedAt: Date.now()
      });
      
      // Update the user's clubId in the 'users' collection if possible
      let userDocIdToUpdate = selectedPlayer?.id;
      if (!userDocIdToUpdate && targetEmail) {
        try {
          const usersSnap = await getDocs(query(firestoreCollection(db, 'users'), where('email', '==', targetEmail)));
          if (!usersSnap.empty) {
            userDocIdToUpdate = usersSnap.docs[0].id;
          }
        } catch(e) {
          console.warn('No se pudo buscar usuario por email', e);
        }
      }

      if (userDocIdToUpdate) {
        try {
          await firestoreUpdateDoc(firestoreDoc(db, 'users', userDocIdToUpdate), {
            clubId: userId || '',
            role: selectedRole
          });
        } catch (e) {
          console.warn('No se pudo actualizar el clubId en users', e);
        }
      }
      
      // Reset form
      setSearchQuery('');
      setSelectedPlayer(null);
      setManualEmail('');
      setSelectedRole('');
    } catch (err) {
      console.error(err);
      alert("Error al agregar al staff");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveStaff = async (email: string) => {
    if (!await window.confirm(`¿Estás seguro de que deseas revocar el acceso a ${email}?`)) return;
    try {
      await firestoreDeleteDoc(firestoreDoc(db, 'staff_roles', email));
    } catch (err) {
      console.error(err);
      alert("Error al eliminar");
    }
  };

  const handleRoleChange = async (email: string, newRole: StaffMember['role']) => {
    try {
      await firestoreUpdateDoc(firestoreDoc(db, 'staff_roles', email), {
        role: newRole,
        updatedAt: Date.now()
      });
    } catch (err) {
      console.error(err);
      alert("Error al actualizar el rol");
    }
  };

  const handlePinChange = async (email: string, newPin: string) => {
    try {
      await firestoreUpdateDoc(firestoreDoc(db, 'staff_roles', email), {
        pin: newPin || null,
        updatedAt: Date.now()
      });
    } catch (err) {
      console.error(err);
      alert("Error al actualizar el PIN");
    }
  };

  const handleAdminPinChange = async (newPin: string) => {
    setAdminPin(newPin); // Optimistic UI
    try {
      await firestoreUpdateDoc(firestoreDoc(db, 'users', userId || ''), {
        posPin: newPin || null
      });
    } catch (err) {
      console.error(err);
      alert("Error al actualizar tu PIN de Admin");
    }
  };

  const roleLabels: Record<string, { label: string, desc: string, icon: any }> = {
    'co_admin': { label: 'Co-Administrador', desc: 'Acceso total a todas las funciones (excepto borrar propietario).', icon: ShieldAlert },
    'empleado': { label: 'Empleado General', desc: 'Acceso a todo excepto balances y métricas financieras.', icon: UserCheck },
    'gestor_turnos': { label: 'Gestor de Turnos', desc: 'Solo gestiona reservas y grilla de canchas.', icon: UserCog },
    'gestor_torneos': { label: 'Gestor de Torneos', desc: 'Solo gestiona torneos, ligas y resultados.', icon: Shield }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white/5 rounded-2xl p-4">
        <h3 className="text-xl font-black uppercase italic tracking-wide text-white mb-2 flex items-center gap-2">
          <Users className="text-amber-400" size={24} />
          Agregar Nuevo Miembro
        </h3>
        <p className="text-white/60 text-sm mb-4">
          Busca un jugador registrado en tu club para otorgarle permisos, o ingresa su correo electrónico manualmente.
        </p>

        <div className="space-y-4">
          {/* SEARCH OR EMAIL */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <label className="block text-xs font-bold text-white/40 uppercase tracking-wider mb-2">
                Buscar Jugador <span className="lowercase text-[9px] text-amber-400/80 tracking-normal ml-1">(recomendado)</span>
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" size={16} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setSelectedPlayer(null);
                    setShowDropdown(true);
                  }}
                  onFocus={() => setShowDropdown(true)}
                  placeholder="Ej: Carlos Perez"
                  className="w-full bg-black/40 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder-white/20 focus:border-amber-400/50 focus:ring-1 focus:ring-amber-400/50 transition-all outline-none"
                />
                {selectedPlayer && (
                  <button 
                    onClick={() => {
                      setSelectedPlayer(null);
                      setSearchQuery('');
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-amber-400 hover:text-amber-300 bg-amber-400/10 p-1 rounded-full"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>

              {/* Autocomplete Dropdown */}
              {showDropdown && searchQuery && !selectedPlayer && (
                <div className="absolute z-10 w-full mt-2 bg-zinc-900 border border-white/10 rounded-xl shadow-2xl overflow-hidden">
                  {filteredPlayers.length > 0 ? (
                    filteredPlayers.map(p => (
                      <button
                        key={p.id}
                        onClick={() => {
                          setSelectedPlayer(p);
                          setSearchQuery(p.name);
                          setShowDropdown(false);
                          setManualEmail('');
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-white/5 flex items-center gap-3 border-b border-white/5 last:border-0 transition-colors"
                      >
                        <div className="w-8 h-8 rounded-full bg-zinc-800 overflow-hidden shrink-0">
                          {p.photoUrl ? (
                            <img src={p.photoUrl} alt={p.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-white/40 text-xs font-bold">
                              {p.name.substring(0, 2).toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-bold text-white">{p.name}</p>
                          <p className="text-[10px] text-white/40">Categoría: {p.category}</p>
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-sm text-white/40 text-center">No se encontraron jugadores</div>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs font-bold text-white/40 uppercase tracking-wider mb-2">
                O ingresar Email (si no es jugador)
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" size={16} />
                <input
                  type="email"
                  value={manualEmail}
                  onChange={(e) => {
                    setManualEmail(e.target.value);
                    setSelectedPlayer(null);
                    setSearchQuery('');
                  }}
                  disabled={!!selectedPlayer}
                  placeholder="ejemplo@correo.com"
                  className="w-full bg-black/40 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder-white/20 focus:border-amber-400/50 focus:ring-1 focus:ring-amber-400/50 transition-all outline-none disabled:opacity-50"
                />
              </div>
            </div>
          </div>

          {/* ROLE SELECTOR */}
          <div>
            <label className="block text-xs font-bold text-white/40 uppercase tracking-wider mb-3">
              Asignar Rol
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {Object.entries(roleLabels).map(([roleKey, data]) => {
                const Icon = data.icon;
                const isSelected = selectedRole === roleKey;
                return (
                  <button
                    key={roleKey}
                    onClick={() => setSelectedRole(roleKey as any)}
                    className={`relative text-left p-3 rounded-xl border transition-all duration-300 ${
                      isSelected 
                        ? 'bg-amber-400/10 border-amber-400/50 shadow-[0_0_15px_rgba(251,191,36,0.15)]' 
                        : 'bg-black/20 border-white/5 hover:border-white/20 hover:bg-white/5'
                    }`}
                  >
                    {isSelected && (
                      <div className="absolute top-2 right-2 text-amber-400">
                        <Check size={16} />
                      </div>
                    )}
                    <Icon className={`mb-3 ${isSelected ? 'text-amber-400' : 'text-white/40'}`} size={24} />
                    <h4 className={`text-sm font-bold mb-1 ${isSelected ? 'text-white' : 'text-white/80'}`}>
                      {data.label}
                    </h4>
                    <p className="text-[10px] text-white/50 leading-relaxed">
                      {data.desc}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="pt-2 flex justify-end">
            <button
              onClick={handleAddStaff}
              disabled={isSubmitting || (!selectedPlayer && !manualEmail) || !selectedRole}
              className="bg-amber-400 text-black px-6 py-2.5 rounded-xl font-black uppercase tracking-wider text-sm hover:bg-amber-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-[0_0_20px_rgba(251,191,36,0.3)]"
            >
              {isSubmitting ? 'Guardando...' : 'Otorgar Acceso al Staff'}
            </button>
          </div>
        </div>
      </div>

      {/* STAFF LIST */}
      <div className="bg-white/5 rounded-2xl p-4">
        <h3 className="text-lg font-black uppercase italic tracking-wide text-white mb-4">
          Equipo Actual ({staffList.length})
        </h3>

        {loading ? (
          <div className="animate-pulse flex space-x-4">
            <div className="flex-1 space-y-4 py-1">
              <div className="h-4 bg-white/10 rounded w-3/4"></div>
              <div className="space-y-2">
                <div className="h-4 bg-white/10 rounded"></div>
                <div className="h-4 bg-white/10 rounded w-5/6"></div>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {/* ADMIN / PROPIETARIO CARD */}
            <div className="bg-amber-400/5 border border-amber-400/20 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 transition-all">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-amber-400/20 flex items-center justify-center shrink-0">
                  <ShieldAlert className="text-amber-400" size={20} />
                </div>
                <div>
                  <p className="text-sm font-bold text-white mb-0.5">Tú (Propietario / Admin)</p>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-amber-400/80 bg-amber-400/10 px-2 py-0.5 rounded-full">
                      Administrador Principal
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto border-t border-white/5 sm:border-t-0 pt-3 sm:pt-0 mt-1 sm:mt-0">
                <div className="flex flex-col">
                  {editingPinId === 'admin' ? (
                    <input
                      type="text"
                      maxLength={4}
                      placeholder="Vacío"
                      defaultValue={adminPin}
                      key={`admin-${adminPin}`}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') e.currentTarget.blur();
                        if (e.key === 'Escape') setEditingPinId(null);
                      }}
                      onBlur={(e) => {
                        const val = e.target.value.replace(/\D/g, '');
                        if (val.length > 0 && val.length < 4) {
                          alert("El PIN debe tener exactamente 4 dígitos o estar vacío.");
                          e.target.value = adminPin;
                          return;
                        }
                        if (val !== adminPin) {
                          handleAdminPinChange(val);
                        }
                        setEditingPinId(null);
                      }}
                      onChange={(e) => e.target.value = e.target.value.replace(/\D/g, '')}
                      className="w-28 bg-black/60 border border-white/10 rounded-lg px-2 py-2 text-center text-xs font-bold text-amber-400 focus:outline-none focus:border-amber-400"
                    />
                  ) : (
                    <button
                      onClick={() => setEditingPinId('admin')}
                      className="w-28 bg-black/40 hover:bg-black/60 border border-white/10 rounded-lg px-2 py-2 text-center text-[11px] font-bold text-zinc-400 hover:text-amber-400 transition-colors"
                      title="Haz clic para habilitar edición de PIN"
                    >
                      {adminPin ? 'PIN: ****' : '+ Habilitar PIN'}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* STAFF LIST */}
            {staffList.length === 0 ? (
              <div className="text-center py-8">
                <ShieldAlert className="mx-auto text-white/20 mb-3" size={32} />
                <p className="text-white/40 text-sm">No hay personal administrativo registrado extra.</p>
              </div>
            ) : (
              staffList.map((staff) => {
                const roleData = roleLabels[staff.role];
                const RoleIcon = roleData?.icon || UserCheck;
                
                // We could match player profile to show picture if we wanted, 
                // but for now let's just show email and role nicely.
                return (
                <div key={staff.email} className="bg-black/40 border border-white/5 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 transition-all hover:bg-white/5 hover:border-white/10">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-amber-400/10 flex items-center justify-center shrink-0">
                      <RoleIcon className="text-amber-400" size={20} />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-white mb-0.5">{staff.email}</p>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-amber-400/80 bg-amber-400/10 px-2 py-0.5 rounded-full">
                          {roleData?.label || staff.role}
                        </span>
                        <span className="text-[10px] text-white/30">
                          Agregado el {new Date(staff.addedAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto border-t border-white/5 sm:border-t-0 pt-3 sm:pt-0 mt-1 sm:mt-0">
                    <div className="flex flex-col">
                      {editingPinId === staff.email ? (
                        <input
                          type="text"
                          maxLength={4}
                          placeholder="Vacío"
                          defaultValue={staff.pin || ''}
                          key={`staff-${staff.email}-${staff.pin || ''}`}
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.currentTarget.blur();
                            if (e.key === 'Escape') setEditingPinId(null);
                          }}
                          onBlur={(e) => {
                            const val = e.target.value.replace(/\D/g, '');
                            if (val.length > 0 && val.length < 4) {
                              alert("El PIN debe tener exactamente 4 dígitos o estar vacío.");
                              e.target.value = staff.pin || '';
                              return;
                            }
                            if (val !== (staff.pin || '')) {
                              handlePinChange(staff.email, val);
                            }
                            setEditingPinId(null);
                          }}
                          onChange={(e) => e.target.value = e.target.value.replace(/\D/g, '')}
                          className="w-28 bg-black/60 border border-white/10 rounded-lg px-2 py-2 text-center text-xs font-bold text-amber-400 focus:outline-none focus:border-amber-400"
                        />
                      ) : (
                        <button
                          onClick={() => setEditingPinId(staff.email)}
                          className="w-28 bg-black/40 hover:bg-black/60 border border-white/10 rounded-lg px-2 py-2 text-center text-[11px] font-bold text-zinc-400 hover:text-amber-400 transition-colors"
                          title="Haz clic para habilitar edición de PIN"
                        >
                          {staff.pin ? 'PIN: ****' : '+ Habilitar PIN'}
                        </button>
                      )}
                    </div>
                    
                    <select
                      value={staff.role}
                      onChange={(e) => handleRoleChange(staff.email, e.target.value as any)}
                      className="bg-black/60 border border-white/10 rounded-lg px-3 py-2 text-xs font-bold text-amber-400 focus:outline-none focus:border-amber-400 cursor-pointer"
                    >
                      {Object.entries(roleLabels).map(([key, data]) => (
                        <option key={key} value={key} className="bg-zinc-900 text-white">{data.label}</option>
                      ))}
                    </select>

                    <button
                      onClick={() => handleRemoveStaff(staff.email)}
                      className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 py-2 bg-red-500/10 text-red-400 hover:bg-red-500/20 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors"
                    >
                      <Trash2 size={14} />
                      Revocar Acceso
                    </button>
                  </div>
                </div>
              );
            }))}
          </div>
        )}
      </div>
    </div>
  );
}
